import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, X as XIcon, Star, Zap, Shield, AlertTriangle, UserCheck, Building2, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

interface PlanTier {
  name: string;
  tagline: string;
  description: string;
  features: string[];
  lockedFeatures?: string[];
  highlighted?: boolean;
  icon: React.ReactNode;
  tierLevel: 'starter' | 'professional' | 'enterprise' | 'government';
}

const SubscriptionTiers: React.FC = () => {
  const tiers: PlanTier[] = [
    {
      name: 'Starter',
      tagline: 'Individual Researchers',
      description: 'Essential OSINT tools for individual researchers and small-scale investigations',
      features: [
        'Core intelligence modules',
        'Basic graph visualization',
        'Standard data sources',
        'PDF report generation',
        'Email support',
      ],
      lockedFeatures: [
        'API access',
        'MCP integration',
      ],
      icon: <Star className="w-6 h-6" />,
      tierLevel: 'starter'
    },
    {
      name: 'Professional',
      tagline: 'Security Teams',
      description: 'Advanced capabilities for professional investigators and security teams',
      features: [
        'All intelligence modules',
        'Advanced graph visualization',
        'Premium data sources',
        'Risk evaluation (PEP screening)',
        'Sanctions & watchlist checks',
        'Criminal exposure analysis',
        'Full REST API access',
        'MCP integration (Claude, Cursor)',
        'Team collaboration',
        'Priority support',
      ],
      highlighted: true,
      icon: <Zap className="w-6 h-6" />,
      tierLevel: 'professional'
    },
    {
      name: 'Enterprise',
      tagline: 'Organizations',
      description: 'Tailored solutions for large organizations with advanced threat intelligence needs',
      features: [
        'All Professional features',
        'Cyber terrorism attribution',
        'Threat actor profiling',
        'Full REST API + webhook push',
        'MCP integration (all AI clients)',
        'Custom request limits',
        'Dedicated account manager',
        'White-label options',
        'Custom integrations & SLA',
        'On-premise deployment',
      ],
      icon: <Building2 className="w-6 h-6" />,
      tierLevel: 'enterprise'
    },
    {
      name: 'Government & LEA',
      tagline: 'Law Enforcement',
      description: 'Specialized capabilities for government agencies and law enforcement',
      features: [
        'All Enterprise features',
        'LEA-specific integrations',
        'Evidence chain of custody',
        'Compliance & audit logging',
        'Classified data handling',
        'Counter-terrorism intelligence',
        'Cross-agency collaboration',
        'Unlimited REST API access',
        'Dedicated API endpoints',
        'MCP integration (air-gapped)',
      ],
      icon: <Shield className="w-6 h-6" />,
      tierLevel: 'government'
    }
  ];

  const getTierStyles = (tierLevel: string, highlighted?: boolean) => {
    if (highlighted) {
      return 'ring-2 ring-green-500 shadow-lg shadow-green-500/30';
    }
    switch (tierLevel) {
      case 'starter': return 'border-gray-500/30';
      case 'professional': return 'border-green-500/40';
      case 'enterprise': return 'border-blue-500/40';
      case 'government': return 'border-yellow-500/40';
      default: return 'border-green-500/20';
    }
  };

  const getIconStyles = (tierLevel: string) => {
    switch (tierLevel) {
      case 'starter': return 'bg-gray-500/10 text-gray-400 border border-gray-500/30';
      case 'professional': return 'bg-green-500/10 text-green-400 border border-green-500/30';
      case 'enterprise': return 'bg-blue-500/10 text-blue-400 border border-blue-500/30';
      case 'government': return 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30';
      default: return 'bg-green-500/10 text-green-400 border border-green-500/30';
    }
  };


  return (
    <div className="py-20 bg-[#0a0f0a] border-t border-green-500/20">
      <div className="container mx-auto px-4">

        {/* ── Header ── */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-green-500/20 text-green-400 border border-green-500/50 font-mono">
            [INTELLIGENCE TIERS]
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Intelligence
            <br />
            <span className="text-green-400">Access Tiers</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">
            Comprehensive OSINT and cyber intelligence capabilities tailored to your operational requirements
          </p>
        </div>

        {/* ── Verified Access Notice ── */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-start gap-4">
            <Lock className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-yellow-400 font-semibold mb-1">Verified Access Only</h4>
              <p className="text-gray-400 text-sm">
                Subscription access is exclusive to verified customers. All users undergo identity verification and vetting
                to ensure responsible use of intelligence capabilities. Contact our team to begin the verification process.
              </p>
            </div>
          </div>
        </div>

        {/* ── Key Capabilities Highlight ── */}
        <div className="max-w-5xl mx-auto mb-16">
          <h3 className="text-center text-2xl font-bold text-white mb-8">
            <span className="text-green-400">Sycek Profiler</span> Advanced Capabilities
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-black/50 border border-red-500/30">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center mb-4 text-red-400">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <h4 className="text-white font-bold mb-2">Risk Evaluation</h4>
                <p className="text-gray-400 text-sm mb-3">
                  Comprehensive risk profiling including PEP (Politically Exposed Persons) screening,
                  sanctions verification, and criminal exposure analysis.
                </p>
                <ul className="space-y-1 text-xs text-gray-500">
                  <li className="flex items-center"><Check className="w-3 h-3 mr-2 text-red-400" />PEP Screening</li>
                  <li className="flex items-center"><Check className="w-3 h-3 mr-2 text-red-400" />Sanctions Checks</li>
                  <li className="flex items-center"><Check className="w-3 h-3 mr-2 text-red-400" />Criminal Exposure</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-black/50 border border-orange-500/30">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center mb-4 text-orange-400">
                  <Shield className="w-6 h-6" />
                </div>
                <h4 className="text-white font-bold mb-2">Extremist Monitoring</h4>
                <p className="text-gray-400 text-sm mb-3">
                  Advanced monitoring and attribution of extremist networks, propaganda propagation, and
                  radicalization pathways across open and dark web sources.
                </p>
                <ul className="space-y-1 text-xs text-gray-500">
                  <li className="flex items-center"><Check className="w-3 h-3 mr-2 text-orange-400" />Network Profiling</li>
                  <li className="flex items-center"><Check className="w-3 h-3 mr-2 text-orange-400" />Propaganda Tracking</li>
                  <li className="flex items-center"><Check className="w-3 h-3 mr-2 text-orange-400" />Network Mapping</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-black/50 border border-purple-500/30">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center mb-4 text-purple-400">
                  <UserCheck className="w-6 h-6" />
                </div>
                <h4 className="text-white font-bold mb-2">Identity Correlation</h4>
                <p className="text-gray-400 text-sm mb-3">
                  Cross-platform identity linking and relationship mapping across multiple data sources
                  for comprehensive subject profiling.
                </p>
                <ul className="space-y-1 text-xs text-gray-500">
                  <li className="flex items-center"><Check className="w-3 h-3 mr-2 text-purple-400" />Cross-Platform Linking</li>
                  <li className="flex items-center"><Check className="w-3 h-3 mr-2 text-purple-400" />Relationship Graphs</li>
                  <li className="flex items-center"><Check className="w-3 h-3 mr-2 text-purple-400" />Pattern Detection</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ── Tier Cards ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {tiers.map((tier) => (
            <Card
              key={tier.name}
              className={`relative bg-black/50 border-green-500/20 ${getTierStyles(tier.tierLevel, tier.highlighted)} hover:border-green-500/50 hover:shadow-xl hover:shadow-green-500/20 transition-all duration-300 flex flex-col h-full`}
            >
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-green-500/20 text-green-400 border border-green-500/50 px-4 py-1 font-mono text-xs">
                    [POPULAR]
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-4">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4 mx-auto ${getIconStyles(tier.tierLevel)}`}>
                  {tier.icon}
                </div>
                <CardTitle className="text-2xl font-bold text-white font-mono">{tier.name}</CardTitle>
                <Badge variant="outline" className="mt-2 border-green-500/30 text-green-400/80 text-xs">
                  {tier.tagline}
                </Badge>
                <p className="text-gray-400 mt-3 text-sm">{tier.description}</p>
              </CardHeader>

              <CardContent className="pt-0 flex flex-col flex-grow">
                <ul className="space-y-3 mb-4 flex-grow">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="w-4 h-4 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Locked / unavailable features */}
                {tier.lockedFeatures && tier.lockedFeatures.length > 0 && (
                  <div className="border-t border-gray-700/50 pt-3 mt-2 space-y-2">
                    {tier.lockedFeatures.map((feature, i) => (
                      <li key={i} className="flex items-start list-none">
                        <XIcon className="w-4 h-4 text-gray-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600 text-sm line-through">{feature}</span>
                      </li>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── Contact CTA ── */}
        <div className="text-center mt-16">
          <div className="bg-black/50 border border-green-500/30 rounded-lg p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">Request Verified Access</h3>
            <p className="text-gray-400 mb-6">
              Contact our intelligence team to discuss your requirements and begin the verification process.
              We'll work with you to determine the right tier for your operational needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                asChild
                className="bg-green-500 hover:bg-green-600 text-black font-semibold shadow-lg shadow-green-500/50 font-mono"
              >
                <Link to="/register">[SIGN UP & REQUEST ACCESS]</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-green-500/50 text-green-400 hover:bg-green-500/10 hover:border-green-400 font-mono"
                onClick={() => window.location.href = 'mailto:support@sycek.io?subject=Subscription%20Access%20Request'}
              >
                [CONTACT INTEL TEAM]
              </Button>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default SubscriptionTiers;
